import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { SELECTED_INSTITUTION_CLEARED_EVENT } from '@/lib/api/client'

interface InstitutionContextValue {
  institutionId: string | null
  setInstitutionId: (id: string | null) => void
}

const InstitutionContext = createContext<InstitutionContextValue | undefined>(undefined)

const STORAGE_KEY = 'legalprime:selectedInstitutionId'

function readStoredInstitutionId(): string | null {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(STORAGE_KEY)
}

function writeStoredInstitutionId(id: string | null): void {
  if (typeof window === 'undefined') return
  if (id) {
    window.localStorage.setItem(STORAGE_KEY, id)
  } else {
    window.localStorage.removeItem(STORAGE_KEY)
  }
}

export function InstitutionProvider({ children }: { children: ReactNode }) {
  const [institutionId, setInstitutionIdState] = useState<string | null>(() =>
    readStoredInstitutionId()
  )

  const setInstitutionId = useCallback((id: string | null) => {
    setInstitutionIdState(id)
    writeStoredInstitutionId(id)
  }, [])

  useEffect(() => {
    const syncClearedSelection = () => {
      setInstitutionIdState(null)
    }
    window.addEventListener(SELECTED_INSTITUTION_CLEARED_EVENT, syncClearedSelection)
    return () => {
      window.removeEventListener(SELECTED_INSTITUTION_CLEARED_EVENT, syncClearedSelection)
    }
  }, [])

  const value = useMemo(
    () => ({
      institutionId,
      setInstitutionId,
    }),
    [institutionId, setInstitutionId]
  )

  return <InstitutionContext.Provider value={value}>{children}</InstitutionContext.Provider>
}

/** Hook para acessar o contexto de instituição. */
// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider (padrão de contexto)
export function useInstitution(): InstitutionContextValue {
  const ctx = useContext(InstitutionContext)
  if (!ctx) {
    throw new Error('useInstitution must be used within InstitutionProvider')
  }
  return ctx
}